import React from 'react';
import Icon from './Icon';
import { showFullTextIfTruncated } from '../../../utils/truncationTooltip';

/**
 * Reusable ResUI chip (PRD-1361, Figma Chip 1051-5208). Geometry lives in
 * `.resui-chip` and each variant's colour pair in `.resui-chip--<variant>`
 * (src/styles/resui-components.css), so every chip in the app looks the same.
 *
 *   <Chip>Draft</Chip>                                   // standard (grey)
 *   <Chip variant="success">Verified</Chip>
 *   <Chip variant="automations">Automation</Chip>        // activity type
 *   <Chip onDismiss={() => remove(id)}>Finance</Chip>    // standard + close target
 *
 * Variants are exactly Figma's. Only Standard has a dismissible form in Figma,
 * so `onDismiss` is ignored on every other variant (with a dev-only warning).
 *
 * A chip never wraps or grows past its container (Design, PRD-1361): it fills
 * the space it has and truncates the label with an ellipsis, showing the full
 * label on hover when it is cut off.
 */
export const CHIP_VARIANTS = [
  // Status
  'standard',
  'success',
  'alert',
  'error',
  'processing',
  // Activity type
  'authentication',
  'data',
  'queries',
  'automations',
  'system',
  'basis',
  'dataset',
];

export default function Chip({
  as: Tag = 'span',
  variant = 'standard',
  onDismiss,
  dismissLabel,
  title,
  className = '',
  children,
  ...props
}) {
  const known = CHIP_VARIANTS.includes(variant) ? variant : 'standard';
  const dismissible = known === 'standard' && typeof onDismiss === 'function';
  if (process.env.NODE_ENV !== 'production' && typeof onDismiss === 'function' && !dismissible) {
    // eslint-disable-next-line no-console
    console.warn(`Chip: only the standard variant is dismissible; onDismiss is ignored on "${known}".`);
  }

  const classes = [
    'resui-chip',
    `resui-chip--${known}`,
    dismissible ? 'resui-chip--dismissible' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag className={classes} title={title} {...props}>
      {/* Full label on hover only when truncated, unless the caller set a title. */}
      <span className="resui-chip-label" onMouseEnter={title ? undefined : showFullTextIfTruncated}>{children}</span>
      {dismissible && (
        <button
          type="button"
          className="resui-chip-dismiss"
          aria-label={dismissLabel || (typeof children === 'string' ? `Remove ${children}` : 'Remove')}
          onClick={onDismiss}
        >
          <Icon name="x" size={12} />
        </button>
      )}
    </Tag>
  );
}
